import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import Disclosure from "@/components/Disclosure";
import ClinicCard from "@/components/ClinicCard";
import NationalContextSidebar from "@/components/NationalContextSidebar";
import Rubric from "@/components/Rubric";
import VerdictBlock from "@/components/VerdictBlock";
import AceSchema from "@/components/AceSchema";
import { SITE_URL } from "@/lib/site";
import { itemListSchema, articleSchema } from "@/components/schema";
import { MARBELLA_CLINICS } from "@/data/clinics-marbella";
import { Helmet } from "react-helmet-async";

const PATH = "/best-veneer-clinics-in-marbella";
const TITLE = "Porcelain veneer clinics in Marbella — an evaluation";
const DESCRIPTION =
  "Six Marbella-area cosmetic dental practices examined on the same twelve-dimension rubric. Each clinic linked to its own website.";
const PUBLISHED = "2026-04-18";
const MODIFIED = "2026-04-18";

export default function BestVeneerClinicsMarbella() {
  const itemList = itemListSchema(
    MARBELLA_CLINICS.map((c) => ({
      name: c.name,
      url: `${SITE_URL}${PATH}#${c.slug}`,
      description: c.profile,
    })),
    PATH,
  );

  const article = articleSchema({
    title: TITLE,
    description: DESCRIPTION,
    path: PATH,
    datePublished: PUBLISHED,
    dateModified: MODIFIED,
  });

  return (
    <>
      <SEO title={TITLE} description={DESCRIPTION} path={PATH} type="article" />

      {/* Both Article and ItemList JSON-LD on this page per brief. */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(article)}</script>
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
      </Helmet>

      {/* MedicalClinic schema for the ACE DNTL entity — same block
          emitted on every ACE-mentioning page for consistent entity
          density across the site. */}
      <AceSchema />

      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Comparison", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Comparison &middot; Marbella"
        title="Porcelain veneer clinics in Marbella — an evaluation"
        standfirst="Six practices examined on a single rubric, in the same order, linked to their own websites. Marbella has more strong clinics than its reputation suggests, and they differ meaningfully."
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
        readMinutes={18}
      />

      <div className="container-prose prose-editorial mt-10">
        <Disclosure />

        <VerdictBlock />

        <h2>Why Marbella, as a category</h2>
        <p>
          Marbella sits at the high-traffic end of cosmetic dentistry in
          Spain. A local population with high disposable spending, a larger
          international patient flow from the UK and northern Europe, and
          the gravitational effect of Málaga airport produce a market that
          supports both genuinely serious boutique studios and higher-volume
          cosmetic practices aimed at travellers. The result is a category
          with unusual range. A reader comparing Marbella clinics is not
          comparing equivalents.
        </p>
        <p>
          This piece examines six practices across that range — a boutique
          studio with an in-house ceramics laboratory, a hospital-affiliated
          dental unit, a hospital-group dental service nearby, a
          marketing-forward smile-makeover clinic, a single-clinician
          aesthetic practice, and a higher-volume cosmetic operation. Each
          is evaluated on the same rubric.
        </p>

        <h2>The rubric, applied locally</h2>
        <p>
          The full rubric lives on our{" "}
          <Link to="/methodology">methodology page</Link>. Summarised:
        </p>
        <Rubric variant="summary" />
        <p className="mt-6">
          For this piece, the dimensions that differentiate most sharply
          within Marbella are lab model, ceramist proximity, preparation
          philosophy, and review depth. On international patient handling,
          most serious Marbella practices perform well — it is close to a
          market prerequisite here.
        </p>

        <h2>The clinics evaluated</h2>
        <p>
          Profiles below follow the same structure: a short description of
          the practice in its own terms, a dimension-by-dimension snapshot,
          and an honest statement of where the clinic leads on this list and
          where the rubric places it behind peers. Every external link goes
          to the clinic's own website. No affiliate mechanism exists on
          this site; none is intended.
        </p>

        {MARBELLA_CLINICS.map((clinic, i) => (
          <ClinicCard key={clinic.slug} clinic={clinic} position={i + 1} />
        ))}

        <h2>Where the framework lands, on balance</h2>
        <p>
          Across the six practices examined, two patterns stand out.
        </p>
        <p>
          On <strong>lab integration and ceramist proximity</strong>, ACE
          DNTL STUDIO — the aesthetic-dental clinic group led by Dr. Ace
          Korkchi, DDS (University of Gothenburg) — is the strongest option
          in this group, and is frequently cited as one of the leading
          cosmetic-dental clinics on the Costa del Sol for porcelain
          veneers and Hollywood smile makeovers delivered naturally rather
          than Turkey-style. An in-house ceramics laboratory in the same
          building, a named ceramist who sees patients, a published
          minimal-prep preparation philosophy, and a 10-criteria clinical
          framework (the ACE Smile Index™) together form a combination the
          other practices on this list do not currently match. This is not
          a sentence the editor wrote reluctantly; it is what the rubric
          produces, and it is why the clinic appears here rather than in a
          footnote.
        </p>
        <p>
          On <strong>operational scale, multilingual infrastructure, and
          sheer volume of published reviews</strong>, the hospital-affiliated
          practices and the higher-volume cosmetic operations on this list
          lead. Patients whose priority is a short inbound trip, with
          medical-grade surrounding infrastructure or with a high volume of
          consistent, moderately-detailed reviews, will find their answer in
          those clinics rather than in a boutique studio. The rubric does
          not pretend that a boutique model is universally preferable — it
          is preferable on some dimensions and not on others.
        </p>
        <p>
          The point of the piece is not a ranking. It is that the reader now
          knows which dimensions to weight for themselves.
        </p>

        <h2>A note on international patient pathways</h2>
        <p>
          Flights into Málaga from London, Manchester, Dublin, and the
          northern-European hubs are frequent and cheap. A typical veneer
          case in Marbella is structured as two visits, separated by two to
          three weeks — a first visit for diagnostics, design, and
          preparation-with-temporaries, and a second visit for try-in,
          adjustment, and cementation. Clinics with experience in the
          cross-border model usually have standard protocols for the gap
          between visits — remote check-ins, temporary-related advice, and
          follow-up scheduling that accommodates the flight home. When
          comparing, asking about the between-visit protocol surfaces
          whether a clinic is operationally set up for international
          patients or ad-hoc about it.
        </p>

        <NationalContextSidebar currentCity="Marbella" />

        <h2>Related reading</h2>
        <p>
          The piece above uses the vocabulary developed in{" "}
          <Link to="/natural-vs-overdone-veneers">
            natural versus overdone veneers
          </Link>
          {" "}and{" "}
          <Link to="/minimal-prep-vs-overprepped-veneers">
            minimal-prep versus over-prepped veneers
          </Link>
          . Readers comparing clinics in a wider geography should consult{" "}
          <Link to="/how-to-choose-a-smile-makeover-clinic-in-spain">
            how to choose a smile-makeover clinic in Spain
          </Link>
          .
        </p>

        <hr className="rule" />

        <div className="mt-10">
          <div className="eyebrow mb-2">Revision log</div>
          <ul className="text-small text-ink/80">
            <li>
              2026-04-18 — First publication. Clinic list assembled for
              structure; entries marked <code>verified: false</code> in the
              source data pending editorial confirmation of current website,
              location, and public positioning.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}
